// src/stores/useNoteStore.js
import { defineStore } from "pinia";
import * as noteService from "@/services/noteService";
import { useActivityStore } from "./useActivityStore";
import { useUserStore } from "./useUserStore";
import {
  compareNotesByTextOrder,
  isDetachedNote,
} from "@/utils/noteOrdering";

// Resolve os ids do foco por trecho contra allNotes, e não contra `notes`: as
// notas de um mesmo trecho podem estar em páginas diferentes da lista, e buscar
// na página traria só as que por acaso já estivessem carregadas. Preserva a
// ordem dos ids recebidos, que é a de notesForHighlight.
function resolveFocusedNotes(state) {
  if (!Array.isArray(state.focusedNoteIds) || state.focusedNoteIds.length === 0) {
    return [];
  }

  const byId = new Map((state.allNotes || []).map((note) => [note.noteId, note]));

  return state.focusedNoteIds.map((noteId) => byId.get(noteId)).filter(Boolean);
}

export const useNoteStore = defineStore("note", {
  state: () => ({
    notes: [],
    allNotes: [],
    selectedNoteIds: [],
    multiSelectedExcerpts: [],
    idNote: null,
    selectedNote: null,
    error: null,
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,
    searchTerm: "",
    shouldForcePageOne: false,
    // ✅ PATCH: controla carregamento das notas para NoteList.vue
    isLoadingNotes: false,

    // --- Filtros existentes ---
    filterIncludeOwn: true,
    filterIncludeComments: false,
    // Ordem da lista: "textOrder" | "newest" | "oldest".
    //
    // O padrão é a ordem do TEXTO — a mesma dos expoentes que o leitor mostra.
    // O número do expoente não é gravado em lugar nenhum: os renderizadores o
    // calculam ordenando por `textRangeEnd` crescente e numerando a partir de 1
    // (TextWithNotes.vue e htmlNoteSegments.js). Ordenar pela mesma chave produz
    // a mesma sequência, sem a lista precisar conhecer os números.
    filterSortOrder: "textOrder",
    filterIncludeSelectedUsers: false,

    // --- Usuários ---
    noteCreators: [],
    selectedUserIdsForFilter: [],

    // >>> NOVO: grupos extraídos das notas carregadas (mesmo critério que noteCreators)
    noteGroups: [],

    // >>> NOVO: filtro por grupo
    filterIncludeSelectedGroups: false,
    selectedGroupIdsForFilter: [],

    // >>> NOVO: filtro por tipo de observação (substitui "Com observação").
    // Array de tipos selecionados (PascalCase), ex.: ["Observation", "Correct"].
    // Vazio = sem filtro. Os três selecionados = equivalente a não restringir.
    selectedObservationTypes: [],

    // >>> NOVO: busca textual LOCAL no campo observação (independente do searchTerm
    // global do SearchNoteInput). Filtra notas cujo observation contém o termo.
    observationSearchTerm: "",

    // >>> NOVO: estado de leitura da observação (quem marca como lida é o CRIADOR
    // da nota). "" = sem filtro | "read" = lidas | "unread" = não lidas.
    // Independente de selectedObservationTypes — combina em AND.
    observationReadState: "",

    // >>> NOVO: filtro "Notas avulsas" (textRangeStart == 0 && textRangeEnd == 0)
    filterOnlyStandalone: false,

    // >>> NOVO: filtros de campo (filtro local sobre allNotes)
    filterByTitle: "", // valor digitado no mini-modal "Por título"
    filterByBody: "", // valor digitado no mini-modal "Por conteúdo"
    filterByTag: "", // valor digitado no mini-modal "Por tag"
    filterBySource: "", // valor digitado no mini-modal "Por fonte"

    // >>> NOVO: filtro por período
    // { field: 'createdAt'|'updatedAt', dateFrom: string (ISO date), dateTo: string (ISO date) }
    filterPeriod: { field: "updatedAt", dateFrom: "", dateTo: "" },

    // --- Permissões ---
    notePermissions: [],

    // --- Contadores ---
    ownNotesCountForActivity: 0,
    othersNotesCountForActivity: 0,

    // --- Toggle do Text Display ---
    isExponentMode: false,
    isHighlightsMode: true,
    // >>> NOVO: exibe/oculta a pilha de avatares dos criadores de notas
    // (NoteCreatorsStack), que flutua sobre a área de leitura.
    isCreatorsStackMode: true,
    // >>> NOVO: exibe/oculta o fólio na margem — a página do documento ORIGINAL,
    // recuperada na importação (ver DocxPageMarks, no backend). Serve para quem
    // tem o impresso na mão citar o mesmo trecho.
    //
    // Nasce DESLIGADO de propósito: só texto importado de arquivo paginado
    // carrega a marca, e ligar por padrão abriria uma faixa vazia em todo o
    // resto da base.
    isPageMarksMode: false,
    // >>> NOVO: noteId a ser aberto em NoteCardModal pelo RightArea
    // após redirecionamento vindo de NotePublicView (link compartilhado).
    // Uso único — RightArea lê e zera imediatamente no onMounted.
    pendingOpenNoteId: null,

    // >>> NOVO: dono (createdByUserId) da nota clicada em "Abrir no contexto"
    // (NoteGeneralForm). NoteSection.loadInitialNotes lê e filtra por esse
    // usuário, em vez do fallback "usuário que mais postou". Uso único.
    pendingContextOwnerUserId: null,

    // >>> CONTEXT-MENU: noteId da ficha a ser iluminada em NoteList (Fluxo 1).
    // TextDisplayer seta; NoteList observa, rola, ilumina e zera.
    highlightNoteId: null,

    // >>> CONTEXT-MENU: noteId cujo trecho deve ser iluminado em TextWithNotes (Fluxo 2).
    // NoteList seta; TextWithNotes observa, rola, ilumina e zera.
    highlightTextNoteId: null,

    // >>> FOCO POR TRECHO: ids das notas que cobrem o ponto clicado no texto.
    // Com ranges sobrepostos, o clique na interseção traz as duas — é o caso que
    // motivou o recurso. Vazio = lista paginada normal.
    //
    // Não é um filtro do sistema de filtros: não vai ao servidor, não mexe em
    // notesForHighlight nem nos destaques do texto. É um recorte local da lista,
    // desfeito pelo botão "ver todas" ou por qualquer refetch da página.
    focusedNoteIds: [],

    // Nota sob o ponteiro no texto. Não é foco nem seleção: não muda o conjunto
    // exibido, só diz por qual ficha a lista deve começar. Quem reage é o
    // NoteList, rolando a coluna — ver `precedência do mouse` lá.
    hoveredNoteId: null,

    // Como o recorte acima foi produzido:
    //
    //   "none"    — sem recorte; a lista é a página vinda da API
    //   "click"   — o usuário clicou num destaque do texto
    //   "reading" — o recorte ACOMPANHA A ROLAGEM: são as notas cujo trecho está
    //               na tela agora
    //
    // O modo existe por causa de um caso que os ids sozinhos não distinguem:
    // seguindo a leitura, um trecho SEM nota nenhuma é resposta legítima — a
    // lista fica vazia, com aviso. Sem o modo, lista vazia seria lida como
    // "não há recorte" e a página inteira voltaria, trocando de 2 itens para 10
    // sem o usuário pedir.
    noteFocusMode: "none",

    // Interruptor de "seguir a leitura". Nasce ligado; desligado, a rolagem do
    // texto deixa de mexer na lista e a paginada volta a ser alcançável.
    followReading: true,
  }),

  getters: {
    isAllSelected: (state) => (total) => {
      return state.selectedNoteIds.length === total;
    },

    // Notas do foco por trecho, resolvidas contra allNotes. Os três getters
    // abaixo passam pelo mesmo helper (fora do store, para não depender de
    // `this` e não quebrar a inferência de tipos dos getters).
    focusedNotes: (state) => resolveFocusedNotes(state),

    // Um foco de CLIQUE que não resolve nenhuma nota (allNotes trocou embaixo
    // dele) não conta como foco — a lista volta a ser a página, sem faixa nem
    // lista vazia. Já o modo de leitura conta mesmo vazio: ali o vazio é o
    // resultado, não a ausência de resultado.
    isNoteFocusActive: (state) =>
      state.noteFocusMode === "reading" ||
      (state.noteFocusMode === "click" && resolveFocusedNotes(state).length > 0),

    // Fonte ÚNICA do que está em exibição na lista. NoteList renderiza daqui e o
    // "marcar todas" do NoteSection monta o lote daqui, senão a seleção em massa
    // pegaria notas que não estão na tela.
    displayedNotes(state) {
      // Seguindo a leitura, o recorte é a resposta mesmo quando é vazio.
      if (state.noteFocusMode === "reading") return resolveFocusedNotes(state);

      const focused = resolveFocusedNotes(state);
      return focused.length > 0 ? focused : state.notes;
    },

    currentNote(state) {
      return state.notes.find((n) => n.noteId === state.idNote) || null;
    },

    notesCountForCurrentActivity(state) {
      return {
        own: state.ownNotesCountForActivity,
        others: state.othersNotesCountForActivity,
        total:
          state.ownNotesCountForActivity + state.othersNotesCountForActivity,
      };
    },

    // =========================================================
    // GETTER notesForHighlight — atualizado com os novos filtros
    // Ordem de aplicação:
    //   1. filterIncludeSelectedUsers / filterIncludeOwn / filterIncludeComments
    //   2. filterIncludeSelectedGroups          (>>> NOVO)
    //   3. selectedObservationTypes             (>>> NOVO)
    //   4. filterByTitle/Body/Tag/Source        (>>> NOVO)
    //   5. filterPeriod                         (>>> NOVO)
    //   6. searchTerm (busca global)
    //   7. filterSortOrder ("textOrder" | "newest" | "oldest")
    // =========================================================
    notesForHighlight(state) {
      let filteredNotes = state.allNotes || [];

      const currentUserId = useUserStore().currentUser?.userId;

      // -------------------------------------------------------
      // 1. Filtros de usuário (existentes)
      // -------------------------------------------------------
      const hasUsersSelected = state.selectedUserIdsForFilter.length > 0;

      // >>> NOVO: "Notas avulsas" resgata o caso (Minhas OFF + Outros OFF).
      // Quando avulsas está ON e nenhum toggle de autoria está marcado (e não há
      // seleção por usuário ativa), pula a exclusão por autoria — deixa passar
      // todas as notas (allNotes já é o escopo permitido do usuário). O filtro de
      // avulsas (3b) restringe o resultado às avulsas.
      const standaloneRescue =
        state.filterOnlyStandalone &&
        !state.filterIncludeOwn &&
        !state.filterIncludeComments &&
        !(state.filterIncludeSelectedUsers && hasUsersSelected);

      if (
        !standaloneRescue &&
        (!state.filterIncludeOwn ||
          !state.filterIncludeComments ||
          (state.filterIncludeSelectedUsers && hasUsersSelected))
      ) {
        filteredNotes = filteredNotes.filter((note) => {
          const isOwnNote = note.createdByUserId === currentUserId;

          if (state.filterIncludeSelectedUsers && hasUsersSelected) {
            return state.selectedUserIdsForFilter.includes(
              note.createdByUserId,
            );
          }

          if (!state.filterIncludeOwn && isOwnNote) return false;
          if (!state.filterIncludeComments && !isOwnNote) return false;
          return true;
        });
      }

      // -------------------------------------------------------
      // 2. >>> NOVO: filtro por grupo
      // Filtra localmente sobre allNotes — mesmo critério do filtro Por usuário.
      // Uma nota passa se tiver ao menos um grupo em groupPermissions que esteja
      // na lista selectedGroupIdsForFilter.
      // -------------------------------------------------------
      if (
        state.filterIncludeSelectedGroups &&
        state.selectedGroupIdsForFilter.length > 0
      ) {
        filteredNotes = filteredNotes.filter((note) => {
          const groups = Array.isArray(note.groupPermissions)
            ? note.groupPermissions
            : [];
          return groups.some((g) =>
            state.selectedGroupIdsForFilter.includes(g.groupId),
          );
        });
      }

      // -------------------------------------------------------
      // 3. >>> NOVO: filtro por tipo de observação
      // "Acertos"/"Erros" são mutuamente exclusivos na UI (no máximo 1 marcado).
      // Vazio (nenhum) = sem restrição por tipo (mostra notas com ou sem observação).
      // -------------------------------------------------------
      const obsTypes = state.selectedObservationTypes;
      if (obsTypes.length > 0) {
        // Certo/Errado são sinalizações explícitas — filtram mesmo sem texto na
        // observação (só o tipo importa aqui, não o preenchimento da descrição).
        filteredNotes = filteredNotes.filter(
          (note) => note.observationType && obsTypes.includes(note.observationType),
        );
      }

      // >>> NOVO: busca textual LOCAL no campo observação (independente do
      // searchTerm global). Combina em AND com o filtro de tipo acima.
      if (state.observationSearchTerm.trim() !== "") {
        const obsTerm = state.observationSearchTerm.trim().toLowerCase();
        filteredNotes = filteredNotes.filter(
          (note) =>
            note.observation && note.observation.toLowerCase().includes(obsTerm),
        );
      }

      // >>> NOVO: estado de leitura da observação. Espelha NoteRepository:
      //   "read"   → isObservationRead (só vira true havendo sinalização).
      //   "unread" → há algo a ler (observationType não-nulo) e ainda não lido.
      // Sem a checagem de observationType, "não lidas" traria toda nota sem
      // observação. Combina em AND com tipo e busca na observação.
      if (state.observationReadState === "read") {
        filteredNotes = filteredNotes.filter((note) => !!note.isObservationRead);
      } else if (state.observationReadState === "unread") {
        filteredNotes = filteredNotes.filter(
          (note) => !!note.observationType && !note.isObservationRead,
        );
      }

      // -------------------------------------------------------
      // 3b. >>> NOVO: filtro "Notas avulsas"
      // Nota avulsa = textRangeStart === 0 && textRangeEnd === 0.
      // Mesmo critério de hasZeroRange() em NoteList.vue (com fallback textRangeStar).
      // -------------------------------------------------------
      if (state.filterOnlyStandalone) {
        filteredNotes = filteredNotes.filter((note) => {
          const start = note.textRangeStart ?? note.textRangeStar ?? null;
          const end = note.textRangeEnd ?? null;
          return start === 0 && end === 0;
        });
      }

      // -------------------------------------------------------
      // 4. >>> NOVO: filtros de campo (título / conteúdo / tag / fonte)
      // Cada filtro é independente e acumulativo (AND).
      // -------------------------------------------------------
      if (state.filterByTitle.trim() !== "") {
        const term = state.filterByTitle.trim().toLowerCase();
        filteredNotes = filteredNotes.filter(
          (note) => note.title && note.title.toLowerCase().includes(term),
        );
      }

      if (state.filterByBody.trim() !== "") {
        const term = state.filterByBody.trim().toLowerCase();
        filteredNotes = filteredNotes.filter(
          (note) => note.body && note.body.toLowerCase().includes(term),
        );
      }

      if (state.filterByTag.trim() !== "") {
        const term = state.filterByTag.trim().toLowerCase();
        filteredNotes = filteredNotes.filter((note) => {
          const tags = Array.isArray(note.tagNames) ? note.tagNames : [];
          return tags.some((t) => t.toLowerCase().includes(term));
        });
      }

      if (state.filterBySource.trim() !== "") {
        const term = state.filterBySource.trim().toLowerCase();
        filteredNotes = filteredNotes.filter(
          (note) => note.source && note.source.toLowerCase().includes(term),
        );
      }

      // -------------------------------------------------------
      // 5. >>> NOVO: filtro por período
      // -------------------------------------------------------
      const { field, dateFrom, dateTo } = state.filterPeriod;
      const hasFrom = dateFrom && dateFrom.trim() !== "";
      const hasTo = dateTo && dateTo.trim() !== "";

      if (hasFrom || hasTo) {
        const fromDate = hasFrom ? new Date(dateFrom + "T00:00:00") : null;
        // "Até" é inclusivo: vai até o final do dia
        const toDate = hasTo ? new Date(dateTo + "T23:59:59") : null;

        filteredNotes = filteredNotes.filter((note) => {
          const raw = field === "updatedAt" ? note.updatedAt : note.createdAt;
          if (!raw) return false;
          const noteDate = new Date(raw);
          if (fromDate && noteDate < fromDate) return false;
          if (toDate && noteDate > toDate) return false;
          return true;
        });
      }

      // -------------------------------------------------------
      // 6. Busca global — searchTerm (existente)
      // -------------------------------------------------------
      if (state.searchTerm.trim() !== "") {
        const rawTerm = state.searchTerm.trim().toLowerCase();
        const termWithoutHash = rawTerm.startsWith("#")
          ? rawTerm.slice(1)
          : rawTerm;

        filteredNotes = filteredNotes.filter((note) => {
          const tagNames = Array.isArray(note.tagNames) ? note.tagNames : [];

          const searchableParts = [
            note.observation,
            note.body,
            note.title,
            note.createdByUserName,
            tagNames.join(" "),
            tagNames.map((t) => `#${t}`).join(" "),
          ];

          const searchable = searchableParts
            .filter((x) => !!x)
            .join(" ")
            .toLowerCase();

          return (
            searchable.includes(rawTerm) || searchable.includes(termWithoutHash)
          );
        });
      }

      // -------------------------------------------------------
      // 7. Ordenação (existente)
      // -------------------------------------------------------
      // ⚠️ Espelho da ordenação do servidor (`NoteRepository.ApplySortOrder`).
      // Este getter alimenta o REALCE do texto; a lista paginada vem ordenada da
      // API. Se os dois discordarem, o desempate entre notas empatadas muda de um
      // lado para o outro — sem erro nenhum.
      if (state.filterSortOrder === "oldest") {
        return [...filteredNotes].sort((a, b) => {
          if (a.createdAt < b.createdAt) return -1;
          if (a.createdAt > b.createdAt) return 1;
          return 0;
        });
      }

      if (state.filterSortOrder === "textOrder") {
        // O mesmo comparador que numera os expoentes nos dois renderizadores.
        return [...filteredNotes].sort(compareNotesByTextOrder);
      }

      return filteredNotes;
    },

    // Getters existentes — sem alteração
    myNotesCount: (state) => {
      const currentUserId = useUserStore().currentUser?.userId;
      if (!currentUserId || !state.allNotes) return 0;
      return state.allNotes.filter(
        (note) => note.createdByUserId === currentUserId,
      ).length;
    },

    // Avulsas = sem trecho ancorado (a convenção 0–0 do projeto, em
    // `noteOrdering.js`). São SUBCONJUNTO das duas contagens de autoria, não uma
    // terceira categoria: uma nota avulsa minha já está em `myNotesCount`. É por
    // isso que elas aparecem indentadas no dropdown, e não como linha irmã —
    // somá-las ao lado das outras daria um total que não fecha.
    myDetachedNotesCount: (state) => {
      const currentUserId = useUserStore().currentUser?.userId;
      if (!currentUserId || !state.allNotes) return 0;
      return state.allNotes.filter(
        (note) => note.createdByUserId === currentUserId && isDetachedNote(note),
      ).length;
    },

    othersDetachedNotesCount: (state) => {
      const currentUserId = useUserStore().currentUser?.userId;
      if (!currentUserId || !state.allNotes) return 0;
      return state.allNotes.filter(
        (note) => note.createdByUserId !== currentUserId && isDetachedNote(note),
      ).length;
    },

    commentsCount: (state) => {
      const currentUserId = useUserStore().currentUser?.userId;
      if (!currentUserId || !state.allNotes) return 0;
      return state.allNotes.filter(
        (note) => note.createdByUserId !== currentUserId,
      ).length;
    },

    // >>> NOVO: usuário (createdByUserId) com mais notas em allNotes; em empate,
    // o de createdAt mais recente. Usado pelo fallback de "Minhas notas vazio".
    mostFrequentNoteCreatorId(state) {
      if (!Array.isArray(state.allNotes) || state.allNotes.length === 0) return null;

      const statsByUser = new Map(); // userId -> { count, lastCreatedAt }
      for (const note of state.allNotes) {
        const userId = note.createdByUserId;
        if (userId === null || userId === undefined) continue;
        const createdAt = note.createdAt ?? "";
        const existing = statsByUser.get(userId);
        if (!existing) {
          statsByUser.set(userId, { count: 1, lastCreatedAt: createdAt });
        } else {
          existing.count += 1;
          if (createdAt > existing.lastCreatedAt) existing.lastCreatedAt = createdAt;
        }
      }

      let bestUserId = null;
      let best = null;
      for (const [userId, stats] of statsByUser) {
        if (
          !best ||
          stats.count > best.count ||
          (stats.count === best.count && stats.lastCreatedAt > best.lastCreatedAt)
        ) {
          best = stats;
          bestUserId = userId;
        }
      }
      return bestUserId;
    },
  },

  actions: {
    clearMultiSelectedExcerpts() {
      this.multiSelectedExcerpts = [];
    },

    setExponentMode(value) {
      this.isExponentMode = value;
    },

    setHighlightsMode(value) {
      this.isHighlightsMode = value;
    },

    setCreatorsStackMode(value) {
      this.isCreatorsStackMode = value;
    },

    setPageMarksMode(value) {
      this.isPageMarksMode = value;
    },

    // --- Contadores ---
    recalculateActivityNoteCounts(activityId = null) {
      const activityStore = useActivityStore();
      const userStore = useUserStore();
      const currentUserId = userStore.currentUser?.userId ?? null;

      const targetActivityId =
        activityId ??
        activityStore.idActivity ??
        activityStore.selectedActivity?.activityId ??
        null;

      if (!targetActivityId) {
        this.ownNotesCountForActivity = 0;
        this.othersNotesCountForActivity = 0;
        return;
      }

      let sourceNotes = [];

      if (
        activityStore.selectedActivity &&
        activityStore.selectedActivity.activityId === targetActivityId &&
        Array.isArray(activityStore.selectedActivity.allNotes)
      ) {
        sourceNotes = activityStore.selectedActivity.allNotes;
      } else if (Array.isArray(this.allNotes) && this.allNotes.length) {
        sourceNotes = this.allNotes.filter(
          (n) => n.activityId === targetActivityId,
        );
      }

      if (!sourceNotes || !sourceNotes.length) {
        this.ownNotesCountForActivity = 0;
        this.othersNotesCountForActivity = 0;
        return;
      }

      let own = 0;
      let others = 0;

      sourceNotes.forEach((note) => {
        if (currentUserId && note.createdByUserId === currentUserId) own++;
        else others++;
      });

      this.ownNotesCountForActivity = own;
      this.othersNotesCountForActivity = others;
    },

    setIdNote(id) {
      this.idNote = id;
    },

    toggleSelection(id) {
      const index = this.selectedNoteIds.indexOf(id);
      if (index > -1) this.selectedNoteIds.splice(index, 1);
      else this.selectedNoteIds.push(id);
    },

    selectAll(ids) {
      this.selectedNoteIds = [...ids];
    },

    clearSelection() {
      this.selectedNoteIds = [];
    },

    toggleUserSelection(userId) {
      const index = this.selectedUserIdsForFilter.indexOf(userId);
      if (index > -1) this.selectedUserIdsForFilter.splice(index, 1);
      else this.selectedUserIdsForFilter.push(userId);
    },

    clearUserSelection() {
      this.selectedUserIdsForFilter = [];
    },

    // >>> FOCO POR TRECHO: chamado pelo TextDisplayer quando um destaque é
    // clicado. Um clique novo substitui o foco anterior.
    focusNotesByIds(noteIds) {
      const ids = (Array.isArray(noteIds) ? noteIds : []).filter(
        (id) => id !== null && id !== undefined,
      );
      this.focusedNoteIds = [...new Set(ids)];
      this.noteFocusMode = "click";
    },

    // >>> SEGUIR A LEITURA: chamado pelo TextDisplayer conforme o texto rola.
    // Recebe os ids das notas cujo trecho está visível, já na ordem do texto.
    //
    // Lista vazia é resultado válido — trecho sem nota nenhuma —, e por isso o
    // modo é marcado mesmo assim. Um clique anterior é sobrescrito de propósito:
    // ficou decidido que a rolagem reassume.
    setReadingVisibleNotes(noteIds) {
      if (!this.followReading) return;

      const ids = (Array.isArray(noteIds) ? noteIds : []).filter(
        (id) => id !== null && id !== undefined,
      );
      this.focusedNoteIds = [...new Set(ids)];
      this.noteFocusMode = "reading";
    },

    setFollowReading(enabled) {
      const wasReading = this.noteFocusMode === "reading";
      this.followReading = !!enabled;

      // Desligar NÃO desfaz o recorte: a lista deve permanecer exatamente
      // como está, com as mesmas fichas na tela, e não saltar para a página
      // paginada (que é outro conjunto, geralmente os primeiros destaques do
      // texto inteiro). Por isso o modo muda para "click" — um recorte
      // parado, como se o usuário tivesse clicado neste trecho — em vez de
      // ser limpo. `focusedNoteIds` não muda; só quem decide seu conteúdo
      // (a rolagem, via setReadingVisibleNotes) para de agir, porque
      // `followReading` já está false.
      //
      // Recorte vazio (nenhuma nota no trecho em leitura) é a exceção: modo
      // "click" com zero notas resolvidas não conta como foco (regra do
      // getter `isNoteFocusActive`), então a lista volta a ser a página
      // normal em vez de ficar presa numa tela vazia sem saída óbvia.
      if (!this.followReading && wasReading) {
        this.noteFocusMode = "click";
      }
    },

    clearNoteFocus() {
      this.focusedNoteIds = [];
      this.noteFocusMode = "none";
    },

    // >>> NOVO: toggle/clear para grupos
    toggleGroupSelection(groupId) {
      const index = this.selectedGroupIdsForFilter.indexOf(groupId);
      if (index > -1) this.selectedGroupIdsForFilter.splice(index, 1);
      else this.selectedGroupIdsForFilter.push(groupId);
    },

    clearGroupSelection() {
      this.selectedGroupIdsForFilter = [];
    },

    // >>> NOVO: reset dos filtros de campo (chamado pelo SearchNoteInput ao digitar)
    resetFieldFilters() {
      this.filterByTitle = "";
      this.filterByBody = "";
      this.filterByTag = "";
      // filterBySource NÃO é resetado pelo searchTerm global (conforme spec)
    },

    // >>> NOVO: reset completo de todos os novos filtros
    resetAllNewFilters() {
      this.selectedObservationTypes = [];
      this.observationSearchTerm = ""; // >>> NOVO
      this.observationReadState = ""; // >>> NOVO
      this.filterOnlyStandalone = false; // >>> NOVO
      this.filterByTitle = "";
      this.filterByBody = "";
      this.filterByTag = "";
      this.filterBySource = "";
      this.filterPeriod = { field: "updatedAt", dateFrom: "", dateTo: "" };
      this.filterIncludeSelectedGroups = false;
      this.selectedGroupIdsForFilter = [];
    },

    // --- AÇÕES DE BUSCA (todas existentes, sem alteração) ---

    async fetchNotes() {
      try {
        const response = await noteService.fetchNotes();
        this.allNotes = response.data;
        this.extractUniqueCreators(this.allNotes);
        this.extractUniqueGroups(this.allNotes); // >>> NOVO
      } catch (error) {
        this.error = error.response?.data || error.message;
        console.error("❌ Erro ao buscar notas:", this.error);
        this.allNotes = [];
      }
    },

    /**
     * Carrega `allNotes` — o conjunto que alimenta o REALCE do texto, não a
     * lista paginada.
     *
     * `sortBy` só é honrado quando vem como string. Vários chamadores antigos
     * passam `false` nesta posição, herança de quando o parâmetro era o booleano
     * `sortByOldest`; sem esse cuidado, `sortBy=false` iria para a query string e
     * o servidor cairia no padrão sem ninguém perceber. A ordem final deste
     * conjunto sai do getter `notesForHighlight`, que reordena pela ordem vigente.
     */
    async getNotesByActivityId(
      activityId,
      includeOwn,
      includeComments,
      sortBy,
      selectedUserIds = [],
      { force = false } = {},
    ) {
      const activityStore = useActivityStore();
      const finalSortBy =
        typeof sortBy === "string" ? sortBy : this.filterSortOrder;

      // `force` pula o atalho de cache. Existe para quando os OFFSETS mudaram no
      // servidor — conversão para HTML, edição do conteúdo —, e aí a cópia
      // guardada na atividade descreve um texto que não existe mais: as notas
      // apontariam para posições da versão anterior e o realce sairia deslocado.
      if (
        !force &&
        activityStore.selectedActivity?.activityId === activityId &&
        activityStore.selectedActivity.allNotes
      ) {
        this.extractUniqueCreators(activityStore.selectedActivity.allNotes);
        await this.fetchFilterableGroupsForActivity(activityId); // >>> NOVO
        this.allNotes = activityStore.selectedActivity.allNotes;
        this.recalculateActivityNoteCounts(activityId);
        return;
      }

      try {
        const response = await noteService.getNotesByActivityId(
          activityId,
          includeOwn,
          includeComments,
          finalSortBy,
          selectedUserIds,
        );
        this.allNotes = response.data;

        // Mantém a cópia da atividade em dia. Sem isto, a próxima chamada SEM
        // `force` cairia no atalho e ressuscitaria as notas velhas — o cache
        // desfaria a atualização que acabou de ser feita.
        if (activityStore.selectedActivity?.activityId === activityId) {
          activityStore.selectedActivity.allNotes = response.data;
        }

        this.extractUniqueCreators(this.allNotes);
        await this.fetchFilterableGroupsForActivity(activityId); // >>> NOVO
        this.recalculateActivityNoteCounts(activityId);
      } catch (error) {
        this.error = error.response?.data || error.message;
        console.error("❌ Erro ao buscar notas por atividade:", this.error);
        this.allNotes = [];
        this.noteCreators = [];
        this.noteGroups = []; // >>> NOVO
        this.recalculateActivityNoteCounts(activityId);
      }
    },

    async fetchNotesByCreatorAndActivity(
      targetUserId,
      activityId,
      includeOnlyOthers = false,
    ) {
      try {
        const response = await noteService.getNotesByCreatorAndActivity(
          targetUserId,
          activityId,
          includeOnlyOthers,
        );
        this.allNotes = response.data;
        this.extractUniqueCreators(this.allNotes);
        await this.fetchFilterableGroupsForActivity(activityId); // >>> NOVO
        return { notes: this.allNotes, count: this.allNotes.length };
      } catch (error) {
        this.error = error.response?.data || error.message;
        console.error(
          "❌ Erro ao buscar notas por criador e atividade:",
          this.error,
        );
        this.allNotes = [];
        this.noteCreators = [];
        this.noteGroups = []; // >>> NOVO
        throw error;
      }
    },

    extractUniqueCreators(notesToProcess) {
      const creatorsMap = new Map();
      notesToProcess.forEach((note) => {
        const userId = note.createdByUserId;
        if (userId && !creatorsMap.has(userId)) {
          const photoPathValue =
            note.createdByUserPhotoPath || note.createdByUser?.photoPath || "";
          creatorsMap.set(userId, {
            id: userId,
            name: note.createdByUserName,
            photoPath: photoPathValue,
          });
        }
      });
      this.noteCreators = Array.from(creatorsMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },

    // >>> NOVO: extrai grupos distintos a partir de groupPermissions das notas carregadas.
    // Mesmo critério de extractUniqueCreators — baseado no conteúdo de allNotes.
    // groupPermissions é o array de GroupListItemDto adicionado ao NoteReadDto.
    extractUniqueGroups(notesToProcess) {
      const groupsMap = new Map();
      notesToProcess.forEach((note) => {
        const groups = Array.isArray(note.groupPermissions)
          ? note.groupPermissions
          : [];
        groups.forEach((g) => {
          if (g.groupId && !groupsMap.has(g.groupId)) {
            groupsMap.set(g.groupId, {
              id: g.groupId,
              name: g.groupName,
            });
          }
        });
      });
      this.noteGroups = Array.from(groupsMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },

    // >>> NOVO: grupos disponíveis para o filtro "Por grupo" — grupos criados pelo
    // usuário logado com algum membro autor de nota nesta atividade. Substitui
    // extractUniqueGroups() (que derivava de groupPermissions/compartilhamento)
    // como fonte de noteGroups para o SelectNoteGroup.vue.
    async fetchFilterableGroupsForActivity(activityId) {
      try {
        const response =
          await noteService.getFilterableGroupsForActivity(activityId);
        this.noteGroups = (response.data || []).map((g) => ({
          id: g.groupId,
          name: g.groupName,
        }));
      } catch (error) {
        console.error("❌ Erro ao buscar grupos filtráveis da atividade:", error);
        this.noteGroups = [];
      }
    },

    // DEPOIS
    async getNoteById(id) {
      try {
        const response = await noteService.getNoteById(id);
        this.selectedNote = response.data;
        this.idNote = id;
        const index = this.notes.findIndex((n) => n.noteId === id);
        if (index === -1) this.notes.push(this.selectedNote);
        else this.notes[index] = this.selectedNote;
        // >>> atualiza allNotes para TextWithNotes refletir mudanças de tag
        const allIndex = this.allNotes.findIndex((n) => n.noteId === id);
        if (allIndex !== -1) this.allNotes[allIndex] = this.selectedNote;
        return response.data;
      } catch (error) {
        const mensagem = error.response?.data || error.message;
        console.error("❌ Erro ao obter nota:", mensagem);
        throw new Error(mensagem);
      }
    },

    async createNote(noteData, activityId) {
      const activityStore = useActivityStore();
      try {
        const response = await noteService.createNote(noteData);
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();
        this.totalCount++;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        // No fallback "Minhas notas vazio" o filtro fica fixado no usuário que mais
        // postou (filterIncludeSelectedUsers + [topUserId]). Ao criar a 1ª nota
        // própria, é preciso incluir o usuário logado no filtro ANTES do fetch
        // paginado — senão a lista rebusca com o filtro antigo e a nova nota não
        // aparece. (push não dispara o watcher raso de NoteSection, então não há
        // refetch corretivo depois.)
        if (this.filterIncludeSelectedUsers) {
          const currentUserId = userStore.currentUser?.userId;
          if (currentUserId && !this.selectedUserIdsForFilter.includes(currentUserId)) {
            this.selectedUserIdsForFilter.push(currentUserId);
          }
        }
        await this.fetchNotesByActivityIdPaged(
          activityId,
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        await activityStore.getActivityById(activityId);
        this.recalculateActivityNoteCounts(activityId);
        return response;
      } catch (error) {
        console.error(
          "❌ Erro ao cadastrar nota na store:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    // >>> NOVO: cria uma nota via IA (endpoint /notes/create-with-ai)
    // Replica a mesma cadeia de atualização do createNote para garantir
    // que allNotes, TextWithNotes e contadores sejam atualizados.
    async createNoteWithAi(payload, activityId) {
      const activityStore = useActivityStore();
      try {
        const response = await noteService.createNoteWithAi(payload);
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();
        this.totalCount++;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        // Ver createNote: inclui o usuário logado no filtro ANTES do fetch paginado
        // para que a nova nota apareça no fallback "usuário que mais postou".
        if (this.filterIncludeSelectedUsers) {
          const currentUserId = userStore.currentUser?.userId;
          if (currentUserId && !this.selectedUserIdsForFilter.includes(currentUserId)) {
            this.selectedUserIdsForFilter.push(currentUserId);
          }
        }
        await this.fetchNotesByActivityIdPaged(
          activityId,
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        await activityStore.getActivityById(activityId);
        this.recalculateActivityNoteCounts(activityId);
        return response;
      } catch (error) {
        console.error(
          "❌ Erro ao criar nota com IA na store:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    async createNotesBatch(noteList, activityId) {
      const activityStore = useActivityStore();
      try {
        const response = await noteService.createNotesBatch(noteList);
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();
        this.totalCount += noteList.length;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        // Ver createNote: inclui o usuário logado no filtro ANTES do fetch paginado
        // para que as novas notas apareçam no fallback "usuário que mais postou".
        if (this.filterIncludeSelectedUsers) {
          const currentUserId = userStore.currentUser?.userId;
          if (currentUserId && !this.selectedUserIdsForFilter.includes(currentUserId)) {
            this.selectedUserIdsForFilter.push(currentUserId);
          }
        }
        await this.fetchNotesByActivityIdPaged(
          activityId,
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        await activityStore.getActivityById(activityId);
        this.recalculateActivityNoteCounts(activityId);
        return response;
      } catch (error) {
        console.error(
          "❌ Erro ao cadastrar notas em lote na store:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    async generateNotesWithAi(payload) {
      // payload: { activityId, tagIds, count, permissions? }
      try {
        const response = await noteService.generateNotesWithAi(payload);
        return response.data; // { createdNotes, createdCount, warnings }
      } catch (error) {
        console.error(
          "❌ Erro ao gerar notas com IA:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    async updateNotesBatch(payload) {
      const activityStore = useActivityStore();
      try {
        const response = await noteService.updateNotesBatch(payload);
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();
        const activityId =
          activityStore.idActivity ??
          activityStore.selectedActivity?.activityId ??
          null;
        if (activityId) {
          await this.fetchNotesByActivityIdPaged(
            activityId,
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
          await activityStore.getActivityById(activityId);
          this.recalculateActivityNoteCounts(activityId);
        } else {
          await this.fetchNotesPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
        }
        return response;
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar notas em lote na store:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    // Edição de campos avulsos a partir do card da lista (título, corpo, tag,
    // fonte). Recebe só o que mudou; o resto vem do servidor.
    //
    // A releitura ANTES de gravar não é zelo, é obrigatória: PUT /notes/{id} é
    // SUBSTITUIÇÃO TOTAL. O serviço sobrescreve observação e trecho com o que
    // vier no corpo, e — o mais grave — APAGA todas as permissões de usuário e
    // de grupo da nota antes de regravar as que o payload trouxer. Um payload
    // com apenas o campo editado tiraria o compartilhamento da nota, zeraria a
    // observação e a desvincularia do trecho, sem erro nenhum na tela.
    //
    // É o mesmo payload que o EditNoteForm monta; a diferença é só de onde vêm
    // os valores não editados.
    async updateNoteFields(noteId, patch = {}) {
      const current = await this.getNoteById(noteId);
      if (!current) {
        throw new Error(`Nota ${noteId} não encontrada.`);
      }

      // As permissões vêm do endpoint próprio, e não da nota: o NoteReadDto
      // devolve quem tem acesso já resolvido para exibição, e não os
      // lançamentos { entityId, type, permissionId } que a gravação espera.
      const grants = await this.fetchNotePermissions(noteId);

      const payload = {
        noteId: current.noteId,
        activityId: current.activityId,
        tagIds: current.tagIds ?? [],
        title: current.title,
        body: current.body,
        source: current.source,
        observation: current.observation,
        isObservationRead: current.isObservationRead,
        textRangeStart: current.textRangeStart,
        textRangeEnd: current.textRangeEnd,
        permissions: (grants || []).map((p) => ({
          entityId: p.userId ?? p.groupId,
          permissionId: p.permissionId,
          type: p.userId != null ? "user" : "group",
        })),
        ...patch,
      };

      return this.updateNote(payload);
    },

    async updateNote(data) {
      const activityStore = useActivityStore();
      try {
        const response = await noteService.updateNote(data);
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();

        // Fora do leitor (ex.: Pesquisa por Notas) não há atividade em contexto:
        // idActivity é null e a recarga por atividade viraria /by-activity-paged/null.
        // O payload da edição traz o activityId da própria nota; sem ele, a tela
        // que abriu o modal é quem recarrega a listagem ao fechar.
        const activityId =
          data?.activityId ??
          activityStore.idActivity ??
          activityStore.selectedActivity?.activityId ??
          null;

        if (activityId) {
          // `preserveFocus`: editar uma nota não é trocar de conjunto. Sem isto
          // o recorte por trecho (clique num destaque ou "seguir a leitura")
          // cairia no meio do salvamento e a lista saltaria para a página
          // inteira — com o card em edição mudando de lugar debaixo do cursor.
          // Os argumentos intermediários são os próprios padrões da ação,
          // repetidos porque a opção vem depois deles.
          await this.fetchNotesByActivityIdPaged(
            activityId,
            this.currentPage,
            this.pageSize,
            this.searchTerm,
            null,
            null,
            null,
            [],
            [],
            { preserveFocus: true },
          );
          await activityStore.getActivityById(activityId);

          // Com o foco preservado a lista é resolvida contra `allNotes` (ver
          // `resolveFocusedNotes`), que a recarga paginada não toca. Adotar
          // aqui a cópia recém-buscada pela atividade é o que faz o card
          // mostrar o texto salvo, e não o anterior. Mesmo gesto do
          // `deleteSelectedNotes`.
          const freshAllNotes = activityStore.selectedActivity?.allNotes;
          if (Array.isArray(freshAllNotes)) {
            this.allNotes = freshAllNotes;
          }

          this.recalculateActivityNoteCounts(activityId);
        }
        return response;
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar nota na store:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    async updateNoteObservation(noteId, observation, observationType = "Observation") {
      const activityStore = useActivityStore();
      try {
        await noteService.updateNoteObservation(noteId, {
          observation,
          observationType,
        });
        const idx = this.notes.findIndex((n) => n.noteId === noteId);
        if (idx !== -1) {
          this.notes[idx].observation = observation;
          this.notes[idx].observationType = observationType;
          this.notes[idx].isObservationRead = false;
        }
        const idxAll = this.allNotes.findIndex((n) => n.noteId === noteId);
        if (idxAll !== -1) {
          this.allNotes[idxAll].observation = observation;
          this.allNotes[idxAll].observationType = observationType;
          this.allNotes[idxAll].isObservationRead = false;
        }
        // Sem atividade em contexto (Pesquisa por Notas), não há o que recarregar.
        if (activityStore.idActivity) {
          await activityStore.getActivityById(activityStore.idActivity);
        }
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar observação da nota:",
          error.message || error,
        );
        throw error;
      }
    },

    async markObservationAsRead(noteId) {
      try {
        await noteService.updateNoteObservationRead(noteId, {
          noteId,
          isObservationRead: true,
        });
        const idx = this.notes.findIndex((n) => n.noteId === noteId);
        if (idx !== -1) this.notes[idx].isObservationRead = true;
        const idxAll = this.allNotes.findIndex((n) => n.noteId === noteId);
        if (idxAll !== -1) this.allNotes[idxAll].isObservationRead = true;
        return true;
      } catch (error) {
        console.error(
          "❌ Erro ao marcar observação como lida (store):",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    async deleteNoteById(id) {
      const activityStore = useActivityStore();
      try {
        await noteService.deleteNote(id);
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();

        // Idem updateNote: fora do leitor não há atividade em contexto.
        const activityId =
          activityStore.idActivity ??
          activityStore.selectedActivity?.activityId ??
          null;
        if (!activityId) return;

        await this.fetchNotesByActivityIdPaged(
          activityId,
          this.notes.length === 1 && this.currentPage > 1
            ? this.currentPage - 1
            : this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        await activityStore.getActivityById(activityId);
        this.recalculateActivityNoteCounts(activityId);
      } catch (error) {
        const mensagem = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir nota:", mensagem);
        throw new Error(mensagem);
      }
    },

    async deleteSelectedNotes() {
      if (!this.selectedNoteIds.length) return;
      const activityStore = useActivityStore();
      try {
        const idsToDelete = [...this.selectedNoteIds];
        await noteService.deleteNotesBatch(idsToDelete);
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();
        this.clearSelection();
        const activityId =
          activityStore.idActivity ??
          activityStore.selectedActivity?.activityId ??
          null;
        if (!activityId) {
          await this.fetchNotesPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
          return;
        }
        await this.fetchNotesByActivityIdPaged(
          activityId,
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        if (this.currentPage > this.totalPages) {
          const safePage = Math.max(1, this.totalPages);
          await this.fetchNotesByActivityIdPaged(
            activityId,
            safePage,
            this.pageSize,
            this.searchTerm,
          );
        }
        await activityStore.getActivityById(activityId);
        const freshAllNotes = Array.isArray(
          activityStore.selectedActivity?.allNotes,
        )
          ? activityStore.selectedActivity.allNotes
          : [];
        this.allNotes = freshAllNotes;
        this.extractUniqueCreators(this.allNotes);
        await this.fetchFilterableGroupsForActivity(activityId); // >>> NOVO
        this.recalculateActivityNoteCounts(activityId);
      } catch (error) {
        const mensagem = error.response?.data || error.message;
        console.error("❌ Erro ao excluir notas em lote:", mensagem);
        throw new Error(mensagem);
      }
    },

    async fetchNotesPaged(pageNumber, pageSize, search) {
      try {
        // ✅ PATCH: inicia loading consumido pelo NoteList.vue
        this.isLoadingNotes = true;

        const response = await noteService.fetchNotesPaged(
          pageNumber,
          pageSize,
          search,
        );

        this.notes = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
      } catch (error) {
        const mensagem = error.response?.data || error.message;
        this.error = mensagem;
        console.error("❌ Erro ao buscar notas paginadas:", mensagem);
        throw new Error(mensagem);
      } finally {
        // ✅ PATCH: finaliza loading mesmo em caso de erro
        this.isLoadingNotes = false;
      }
    },

    async setSearchTerm(term) {
      this.searchTerm = term;
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      await this.setNoteFilters({
        includeOwn: this.filterIncludeOwn,
        comments: this.filterIncludeComments,
        sortBy: this.filterSortOrder,
        includeSelectedUsers: this.filterIncludeSelectedUsers,
      });
    },

    async fetchNotesByActivityIdPaged(
      activityId,
      page = 1,
      size = 10,
      search = "",
      includeOwn = null,
      includeComments = null,
      sortBy = null,
      selectedUserIds = [],
      selectedGroupIds = [],
      // Recarga que NÃO é troca de conjunto — hoje só a que segue o salvamento
      // de uma nota (ver `updateNote`). A lista volta com os mesmos critérios
      // de antes, então derrubar o recorte por trecho seria desfazer, de
      // lambuja, uma escolha que o usuário não mexeu.
      { preserveFocus = false } = {},
    ) {
      // Fora do `try` de propósito: são lidos depois do `finally`, por
      // `goToFirstMatchInText`, e dentro do bloco sairiam de escopo.
      //
      // O modo importa tanto quanto o conjunto: na CARGA INICIAL ainda não há
      // recorte de leitura nenhum, e sem esta distinção o texto saltaria para a
      // primeira nota assim que fosse aberto — que não é filtrar, é abrir.
      const wasFollowingReading = this.noteFocusMode === "reading";
      const visibleBeforeFetch = new Set(this.focusedNoteIds);

      try {
        // ✅ PATCH: inicia loading consumido pelo NoteList.vue
        this.isLoadingNotes = true;

        // Ponto único por onde a lista é trocada: mudar de página, aplicar
        // filtro, buscar ou criar nota desfaz o foco por trecho. Sem isto a
        // faixa "exibindo N notas" sobreviveria a uma lista que já é outra.
        //
        // `visibleBeforeFetch`, capturado acima, guarda as notas que estavam na
        // tela ANTES de o foco cair: é o que permite decidir, no fim, se a
        // leitura precisa ser levada até a primeira ocorrência ou se ela já está
        // à vista. Depois deste `clearNoteFocus` a informação não existe mais.
        if (!preserveFocus) this.clearNoteFocus();

        this.searchTerm = search;
        this.pageSize = size;
        this.currentPage = page;
        const finalIncludeOwn = includeOwn ?? this.filterIncludeOwn;
        const finalIncludeComments =
          includeComments ?? this.filterIncludeComments;
        const finalSortBy = sortBy ?? this.filterSortOrder;
        const finalSelectedUserIds =
          selectedUserIds.length > 0
            ? selectedUserIds
            : this.filterIncludeSelectedUsers
            ? this.selectedUserIdsForFilter
            : [];
        const finalSelectedGroupIds =
          selectedGroupIds.length > 0
            ? selectedGroupIds
            : this.filterIncludeSelectedGroups
            ? this.selectedGroupIdsForFilter
            : [];
        // >>> NOVO: tipos de observação — envia sempre que houver 1+ selecionado.
        // O backend filtra só por tipo IN (Certo/Errado valem mesmo sem descrição).
        const finalObservationTypes =
          this.selectedObservationTypes.length > 0
            ? this.selectedObservationTypes
            : [];
        // >>> NOVOS: filtros de campo e período lidos diretamente da store
        const response = await noteService.fetchNotesByActivityIdPaged(
          activityId,
          page,
          size,
          search,
          finalIncludeOwn,
          finalIncludeComments,
          finalSortBy,
          finalSelectedUserIds,
          finalSelectedGroupIds,
          finalObservationTypes,
          this.filterByTitle,
          this.filterByBody,
          this.filterByTag,
          this.filterBySource,
          this.filterPeriod.field,
          this.filterPeriod.dateFrom,
          this.filterPeriod.dateTo,
          this.filterOnlyStandalone, // >>> NOVO: filtro "Notas avulsas"
          this.observationSearchTerm, // >>> NOVO: busca local na observação
          this.observationReadState, // >>> NOVO: leitura da observação (read/unread)
        );
        const data = response.data;
        this.notes = data.data;
        this.totalCount = data.totalCount;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
      } catch (error) {
        console.error("❌ Erro ao buscar notas paginadas:", error);
        this.notes = [];
        this.totalCount = 0;
        this.currentPage = 1;
        this.totalPages = 1;
      } finally {
        // ✅ PATCH: finaliza loading mesmo em caso de erro
        this.isLoadingNotes = false;
      }

      // Com o foco preservado não houve busca nem filtro: o recorte é o mesmo
      // de antes e não há "primeira ocorrência" nova para onde levar a leitura.
      if (wasFollowingReading && !preserveFocus) {
        this.goToFirstMatchInText(visibleBeforeFetch);
      }
    },

    /**
     * Leva a leitura até a primeira nota que casou com o filtro ou a busca.
     *
     * Existe por causa de um efeito de "seguir a leitura": a busca sempre correu
     * o texto INTEIRO — `notesForHighlight` filtra todas as notas, e a lista
     * paginada consulta o servidor sobre todas —, mas o resultado ficava
     * invisível quando as notas que casaram estavam noutro ponto do texto. A
     * coluna dizia "nenhuma nota neste trecho" e a busca parecia não ter achado
     * nada.
     *
     * Chamado apenas quando a leitura JÁ estava sendo acompanhada — abrir o
     * texto não é filtrar, e não deve mover ninguém.
     *
     * Só age quando NENHUMA das notas que casaram está na tela. Se alguma já
     * está, mover a leitura seria arrastar o usuário para longe do que ele
     * estava lendo, sem ganho. É também o que evita o salto ao LIMPAR um filtro:
     * o conjunto volta a ser grande e quase sempre inclui algo à vista.
     *
     * A rolagem em si reusa `highlightTextNoteId`, o caminho que os dois
     * renderizadores já implementam para o clique numa nota da lista.
     */
    goToFirstMatchInText(visibleNoteIds) {
      if (!this.followReading) return;

      const matches = this.notesForHighlight;
      if (!matches || matches.length === 0) return;

      const visible = visibleNoteIds instanceof Set ? visibleNoteIds : new Set();
      if (matches.some((note) => visible.has(note.noteId))) return;

      // O comparador põe as avulsas por último, então a primeira é ancorada —
      // a menos que TODAS sejam avulsas, e aí não há onde posicionar o texto.
      const first = [...matches].sort(compareNotesByTextOrder)[0];
      if (!first || isDetachedNote(first)) return;

      this.highlightTextNoteId = first.noteId;
    },

    // >>> NOVO: regra ÚNICA de aplicação do filtro por usuário, compartilhada
    // pelo NoteFilter (via SelectNoteUser) e pela pilha de criadores
    // (NoteCreatorsStack). Antes vivia apenas no applyUserFilter do NoteFilter;
    // com dois pontos de entrada, duplicá-la faria as duas superfícies
    // divergirem para a MESMA seleção de usuários.
    // Só mexe no estado — o refetch continua a cargo de quem chama.
    applyUserSelectionFilter({ userIds = null, selectionApplied = true } = {}) {
      if (Array.isArray(userIds)) {
        this.selectedUserIdsForFilter = [...userIds];
      }

      const hasUsers = this.selectedUserIdsForFilter.length > 0;
      this.filterIncludeSelectedUsers = selectionApplied && hasUsers;

      if (!this.filterIncludeSelectedUsers) {
        this.selectedUserIdsForFilter = [];
      } else {
        // Filtro por usuário é mais específico: desmarca Minhas notas/Notas de outros
        this.filterIncludeOwn = false;
        this.filterIncludeComments = false;
      }
    },

    // >>> NOVO: as DUAS dimensões do popover da pilha de criadores numa só
    // chamada. A regra de cada uma continua sendo a que já existia — esta para
    // usuário, e a do applyGroupFilter (NoteFilter.vue) para grupo —, reunidas
    // aqui para que o popover não precise repetir a sequência de grupo e
    // divergir do NoteFilter depois. Só mexe no estado; o refetch é de quem chama.
    applyCreatorSelectionFilter({ userIds = null, groupIds = null } = {}) {
      this.applyUserSelectionFilter({ userIds });

      if (Array.isArray(groupIds)) {
        this.selectedGroupIdsForFilter = [...groupIds];
      }

      const hasGroups = this.selectedGroupIdsForFilter.length > 0;
      this.filterIncludeSelectedGroups = hasGroups;
      if (!hasGroups) this.clearGroupSelection();

      // Sem nenhuma das duas dimensões, o recorte por criador sai de cena e a
      // autoria volta ao padrão. Sem isto sobraria o estado morto que o
      // applyUserSelectionFilter deixa ao zerar filterIncludeOwn/Comments:
      // nenhuma autoria, nenhum usuário e nenhum grupo — lista vazia sem que
      // nada na tela explique o porquê. É o caminho do botão "Limpar".
      if (
        !this.filterIncludeSelectedUsers &&
        !hasGroups &&
        !this.filterIncludeComments
      ) {
        this.filterIncludeOwn = true;
      }
    },

    async setNoteFilters(filters) {
      const activityStore = useActivityStore();
      const currentActivityId = activityStore.idActivity;

      this.filterIncludeOwn = filters.includeOwn;
      this.filterIncludeComments = filters.comments;
      this.filterSortOrder = filters.sortBy;
      this.filterIncludeSelectedUsers = filters.includeSelectedUsers;
      // >>> NOVO: sincroniza flag de grupo se vier no objeto filters
      if (typeof filters.includeSelectedGroups !== "undefined") {
        this.filterIncludeSelectedGroups = filters.includeSelectedGroups;
      }

      const finalSelectedUserIds = filters.includeSelectedUsers
        ? this.selectedUserIdsForFilter
        : [];

      // >>> NOVO: resolve grupos do filtro
      const finalSelectedGroupIds = this.filterIncludeSelectedGroups
        ? this.selectedGroupIdsForFilter
        : [];

      await this.getNotesByActivityId(currentActivityId, true, true, false, []);

      this.totalCount = this.allNotes.length;
      this.totalPages = Math.ceil(this.totalCount / this.pageSize);
      this.currentPage = 1;

      this.recalculateActivityNoteCounts(currentActivityId);

      await this.fetchNotesByActivityIdPaged(
        currentActivityId,
        this.currentPage,
        this.pageSize,
        this.searchTerm,
        this.filterIncludeOwn,
        this.filterIncludeComments,
        this.filterSortOrder,
        finalSelectedUserIds,
        finalSelectedGroupIds, // >>> NOVO
      );
    },

    async resetNoteFiltersToDefault() {
      this.filterIncludeOwn = true;
      this.filterIncludeComments = false;
      this.filterSortOrder = "textOrder";
      this.filterIncludeSelectedUsers = false;
      this.clearUserSelection();

      // >>> NOVO: reseta também os novos filtros
      this.resetAllNewFilters();

      const activityStore = useActivityStore();
      const currentActivityId = activityStore.idActivity;
      if (currentActivityId) {
        await this.fetchNotesByActivityIdPaged(
          currentActivityId,
          this.currentPage,
          this.pageSize,
          this.searchTerm,
          this.filterIncludeOwn,
          this.filterIncludeComments,
          this.filterSortOrder,
          [],
        );
        this.recalculateActivityNoteCounts(currentActivityId);
      }
    },

    // --- Permissões ---
    async fetchNotePermissions(noteId) {
      try {
        const response = await noteService.fetchNotePermissions(noteId);
        this.notePermissions = response.data;
        return response.data;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.notePermissions = [];
          return [];
        }
        console.error("❌ Erro ao buscar permissões da nota:", error.message);
        this.notePermissions = [];
        throw error;
      }
    },

    async addNotePermission(noteId, permissionData) {
      try {
        await noteService.addNotePermission(noteId, permissionData);
        await this.fetchNotePermissions(noteId);
      } catch (error) {
        console.error("❌ Erro ao adicionar permissão na nota:", error.message);
        throw error;
      }
    },

    async removeNotePermission(noteId, userId, permissionId) {
      try {
        await noteService.removeNotePermission(noteId, userId, permissionId);
        await this.fetchNotePermissions(noteId);
      } catch (error) {
        console.error("❌ Erro ao remover permissão da nota:", error.message);
        throw error;
      }
    },
  },
});
